import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Collapse,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ChevronDown, ChevronUp, FileText, Image as ImageIcon } from 'lucide-react';
import type { SourceCitation } from '../types/chat.types';
import { useGetPageImageUrl } from './SourceCitations.mutations';

interface SourceCitationsProps {
  sources: SourceCitation[];
  compact?: boolean;
}

interface PageImageState {
  loading: boolean;
  url: string | null;
  error: boolean;
  attempted: boolean;
}

const SourceCitations: React.FC<SourceCitationsProps> = ({ sources, compact = false }) => {
  const [expandedTextIndex, setExpandedTextIndex] = useState<number | null>(null);
  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(null);
  const [pageImages, setPageImages] = useState<Record<number, PageImageState>>({});
  const getPageImageUrl = useGetPageImageUrl();

  const canShowPageImage = useCallback((source: SourceCitation): boolean => {
    return !!(source.source_id && source.page && source.page !== 'N/A');
  }, []);

  const handleTogglePageImage = useCallback(
    (index: number, source: SourceCitation) => {
      const existing = pageImages[index];

      // If already attempted (loaded or failed), just toggle the image panel
      if (existing?.attempted) {
        setExpandedImageIndex((prev) => (prev === index ? null : index));
        return;
      }

      // Prevent duplicate concurrent fetches
      if (existing?.loading) return;

      if (!source.source_id || !source.page || source.page === 'N/A') return;

      const pageNum = typeof source.page === 'string' ? parseInt(source.page, 10) : source.page;
      if (isNaN(pageNum) || pageNum < 1) return;

      // Start loading
      setPageImages((prev) => ({
        ...prev,
        [index]: { loading: true, url: null, error: false, attempted: false },
      }));
      setExpandedImageIndex(index);

      getPageImageUrl.mutate(
        { sourceId: source.source_id, page: pageNum },
        {
          onSuccess: (result) => {
            setPageImages((prev) => ({
              ...prev,
              [index]: {
                loading: false,
                url: result.url,
                error: !result.url,
                attempted: true,
              },
            }));
          },
          onError: () => {
            setPageImages((prev) => ({
              ...prev,
              [index]: { loading: false, url: null, error: true, attempted: true },
            }));
          },
        }
      );
    },
    [pageImages, getPageImageUrl]
  );

  if (!sources.length) return null;

  const displaySources = compact ? sources.slice(0, 3) : sources;

  const getSimilarityColor = (similarity: number): 'success' | 'warning' | 'default' => {
    if (similarity >= 0.7) return 'success';
    if (similarity >= 0.5) return 'warning';
    return 'default';
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Sources ({sources.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {displaySources.map((source, i) => {
          const imageState = pageImages[i];
          const hasPageImage = canShowPageImage(source);
          const isTextExpanded = expandedTextIndex === i;
          const isImageExpanded = expandedImageIndex === i;

          return (
            <Paper key={i} variant="outlined" sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileText size={14} />
                <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
                  {source.document_title}
                </Typography>
                <Chip
                  label={source.document_type}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
                <Chip
                  label={`${(source.similarity * 100).toFixed(0)}%`}
                  size="small"
                  color={getSimilarityColor(source.similarity)}
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
                {hasPageImage && (
                  <Tooltip title="View page image">
                    <IconButton
                      size="small"
                      onClick={() => handleTogglePageImage(i, source)}
                      color={isImageExpanded && imageState?.url ? 'primary' : 'default'}
                    >
                      {imageState?.loading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <ImageIcon size={14} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onClick={() => setExpandedTextIndex(isTextExpanded ? null : i)}
                >
                  {isTextExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </IconButton>
              </Box>
              {source.page && (
                <Typography variant="caption" color="text.secondary">
                  Page {source.page}
                  {source.version ? ` | v${source.version}` : ''}
                  {source.drone_model ? ` | ${source.drone_model}` : ''}
                </Typography>
              )}
              {/* Page image preview — separate from text expand */}
              <Collapse in={isImageExpanded}>
                <Box sx={{ mt: 0.5 }}>
                  {imageState?.attempted && imageState.url && (
                    <Box
                      sx={{
                        mb: 1,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'grey.100',
                      }}
                    >
                      <Box
                        component="img"
                        src={imageState.url}
                        alt={`Page ${source.page} of ${source.document_title}`}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          maxHeight: 500,
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  )}
                  {imageState?.attempted && imageState.error && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Page image not available
                    </Typography>
                  )}
                </Box>
              </Collapse>
              {/* Text content expand */}
              <Collapse in={isTextExpanded}>
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                  >
                    {source.content}
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
      {compact && sources.length > 3 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          +{sources.length - 3} more sources
        </Typography>
      )}
    </Box>
  );
};

export default SourceCitations;
